import { BleCharacteristic, BleClient, numbersToDataView, ScanResult } from '@capacitor-community/bluetooth-le';
import { Message, MessageService } from '@ncss/message';
import { ConversionUtils, DeviceTypeHelper, DeviceTypeIds, DeviceTypes, IDeviceType, TR4E } from '@ncss/models';
import { NcssEncryption, NcssEncryptionVersion } from 'ncss-encryption';
import { ByteList } from 'byte-list';
import { shallowRef, ShallowRef } from 'vue';
import { BleRssiToSignalStrength, BleSignalStrengthToColor, BleSignalStrengthToImgUrl } from './utils';
import { captureException } from '@sentry/vue';
import { DeviceTypeToImgUrl } from '@/utils/device.utils';
import { isPlatform } from '@ionic/vue';

const AVG_SIGNAL_OVER_SECONDS = 10;
const NordicId = 0x0059;
const DEFAULT_TIMEOUT_SECONDS = 5;
const DEFAULT_RETRY_COUNT = 2;

export const BLE_SERVICES = {
  GW301: {
    ID: '49535343-FE7D-4AE5-8FA9-9FAFD205E455',
    CHARACTERISTICS: {
      NOTIFY: '49535343-1E4D-4BD9-BA61-23C647249616',
      WRITE: '49535343-1E4D-4BD9-BA61-23C647249616',
    },
  },
  NCSS_CONNECT: {
    ID: '8667000A-AB40-461B-AC61-BAFA8D71BB86',
    CHARACTERISTICS: {
      NOTIFY: '86670003-AB40-461B-AC61-BAFA8D71BB86',
      WRITE: '86670002-AB40-461B-AC61-BAFA8D71BB86',
    },
  },
  DIRECT_CONNECT: {
    ID: '6E40000A-B5A3-F393-E0A9-E50E24DCCA9E',
    CHARACTERISTICS: {
      NOTIFY: '6E400003-B5A3-F393-E0A9-E50E24DCCA9E',
      WRITE: '6E400002-B5A3-F393-E0A9-E50E24DCCA9E',
    },
  },

  WALK_BY: {
    ID: '6E400A00-B5A3-F393-E0A9-E50E24DCCA9E',
    CHARACTERISTICS: {
      NOTIFY: '6E400A01-B5A3-F393-E0A9-E50E24DCCA9E',
      WRITE: '6E400A02-B5A3-F393-E0A9-E50E24DCCA9E',
      MTU: '6E400A03-B5A3-F393-E0A9-E50E24DCCA9E',
      PDU: '6E400A04-B5A3-F393-E0A9-E50E24DCCA9E',
      PHY: '6E400A05-B5A3-F393-E0A9-E50E24DCCA9E',
    },
  },
};

const ManufacturerDataDeviceTypes = [
  DeviceTypeIds.GW4,
  DeviceTypeIds.GW4_LITE,
  DeviceTypeIds.GATEWAY_301,
  DeviceTypeIds.RR4,
  DeviceTypeIds.RR4_TR,
  DeviceTypeIds.RE4,
  // DeviceTypeIds.NR4,
  DeviceTypeIds.DIRECT_CONNECT_PROGRAMMER,
  DeviceTypeIds.NL4,
];

const NonManufacturerDataDeviceNames = ['GW301', 'RR3', 'TR4I', 'DC301', 'TR4E'];
const DeviceNameToType: { [name: string]: IDeviceType } = {
  GW301: DeviceTypes[DeviceTypeIds.GATEWAY_301],
  RR3: DeviceTypes[DeviceTypeIds.REMOTE_READER],
  TR4I: DeviceTypes[DeviceTypeIds.TR4_I],
  TR4E: DeviceTypes[DeviceTypeIds.TR4_E],
  DC301: DeviceTypes[DeviceTypeIds.DIRECT_CONNECT_PROGRAMMER],
};

const NOTIFICATION_GATT_HEADER_SIZE = 3;
const L2CAP_HEADER_SIZE = 4;
const DEFAULT_MTU_SIZE = 23;
const DEFAULT_PDU_SIZE = 27;
const IFS_TIME_US = 150;

export interface VisibleDevice {
  bleDeviceId: string;
  deviceId: number;
  deviceType: IDeviceType;
  manufacturerData: ByteList;
  rssi: number;
}

enum BleAntenna {
  UNKNOWN = 0,
  PHY_1MBPS = 1,
  PHY_2MBPS = 2,
}

type ConnectionState = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'ERROR';

export interface DirectConnectDeviceConstructor<T extends DirectConnectBaseDevice = DirectConnectBaseDevice> {
  new(info: VisibleDevice, useMetric: boolean): T;
}

interface Waiter<T extends Message = Message> {
  resolve: (res?: T) => void;
  match: (res: T) => boolean;
  timeOutAt: Date;
}

export abstract class DirectConnectBaseDevice {
  private static DeviceTypes: { [deviceTypeId: string]: DirectConnectDeviceConstructor } = {};

  static Create(device: VisibleDevice, useMetric: boolean) {
    if (this.DeviceTypes[device.deviceType.id]) {
      return new this.DeviceTypes[device.deviceType.id](device, useMetric);
    } else {
      console.warn('Found a valid device type but no registered constructor in DirectConnectBaseDevice', device);
    }
    return null;
  }

  static RegisterType<T extends DirectConnectBaseDevice>(
    deviceTypeId: DeviceTypeIds,
    constructor: DirectConnectDeviceConstructor<T>
  ) {
    this.DeviceTypes[deviceTypeId] = constructor;
  }

  static CreateVisibleDevice(scanResult: ScanResult): VisibleDevice | null {
    let foundDeviceName: string | undefined = undefined;
    if (!scanResult || !scanResult.manufacturerData || !scanResult.manufacturerData[NordicId]) {
      foundDeviceName = this.FindValidDeviceName(scanResult);
      if (!foundDeviceName) {
        return null;
      }
    }

    let res: { deviceId: number; deviceType: IDeviceType } | null = null;
    let manufacturerData: ByteList = scanResult.manufacturerData?.[NordicId]
      ? new ByteList(scanResult.manufacturerData[NordicId].buffer as ArrayBuffer)
      : new ByteList();

    if (foundDeviceName) {
      res = this.CheckDeviceName(foundDeviceName, scanResult);
    } else {
      res = this.CheckDeviceManufacturerData(manufacturerData, scanResult);
    }

    return res?.deviceId
      ? { ...res, manufacturerData, rssi: scanResult.rssi || 0, bleDeviceId: scanResult.device.deviceId }
      : null;
  }

  private static FindValidDeviceName(scanResult: ScanResult) {
    if (!scanResult.device.name) {
      return undefined;
    }
    return NonManufacturerDataDeviceNames.find((name) => scanResult.device.name?.startsWith(name));
  }

  private static CheckDeviceManufacturerData(byteList: ByteList, scanResult: ScanResult) {
    if (byteList.length < 4) {
      return null;
    }
    const readOffDevice = (bytes: ByteList) => {
      let deviceId = bytes.peekUInt32();
      let deviceType = DeviceTypeHelper.GetDeviceTypeBySerialNumber(deviceId);
      if (deviceType && ManufacturerDataDeviceTypes.includes(deviceType.id)) {
        bytes.index += 4; // Shift over to account for the valid serial number that was read
        return { deviceId, deviceType };
      }
      return null;
    };

    if (isPlatform('ios') && byteList.peekUInt16() === NordicId) {
      const index = byteList.index;
      byteList.index += 2; // skip forward 2 bytes (nordicId)
      const r = readOffDevice(byteList);
      if (r) {
        return r;
      }
      byteList.index = index;
    }
    const result = readOffDevice(byteList);
    if (result) {
      return result;
    }
    if (byteList.length >= 6) {
      const index = byteList.index;
      byteList.index += 2; // skip forward 2 bytes (nordicId)
      const result = readOffDevice(byteList);
      if (result) {
        return result;
      }
      byteList.index = index;
    }
    return null;
  }

  private static CheckDeviceName(foundDeviceName: string, scanResult: ScanResult) {
    if (!DeviceNameToType[foundDeviceName]) {
      return null;
    }
    const deviceType = DeviceNameToType[foundDeviceName];
    const deviceIdStr = scanResult.device.name?.replace(foundDeviceName + '-', '');
    if (!deviceIdStr) {
      return null;
    }
    const deviceId = ConversionUtils.ConvertSerialNumberToNumber(deviceIdStr);
    if (!deviceId) {
      return null;
    }
    return { deviceId, deviceType };
  }

  bleDeviceId: string;
  deviceId: number;
  deviceType: IDeviceType;
  deviceTypeImg?: string;
  bleSignalStrength: ShallowRef<number>;
  bleSignalStrengthImg: ShallowRef<string>;
  bleSignalStrengthColor: ShallowRef<string>;
  manufacturerData: ByteList;
  createdAt: Date;
  updatedAt?: Date;
  connectionState = shallowRef<ConnectionState>('DISCONNECTED');
  protected messageService: MessageService;
  protected useEncryption?: boolean;
  protected encryptionKey?: NcssEncryptionVersion;
  protected abstract serviceId: string;
  protected abstract notifyCharacteristicId: string;
  protected abstract writeCharacteristicId: string;
  protected useMetric: boolean;
  protected periodicChecksTimeout?: NodeJS.Timeout;

  private signalStrengthHistory: { value: number; timestamp: Date }[] = [];
  private txBytes = new ByteList();
  private waitingForResponse: Waiter[] = [];
  private pduSize = DEFAULT_PDU_SIZE;
  private mtuSize = DEFAULT_MTU_SIZE;
  private txSize = DEFAULT_MTU_SIZE - NOTIFICATION_GATT_HEADER_SIZE;
  private phy = BleAntenna.UNKNOWN;
  private sendTimeUs = DEFAULT_PDU_SIZE * 8 + IFS_TIME_US;

  constructor(info: VisibleDevice, useMetric: boolean) {
    this.messageService = new MessageService();
    this.messageService.numberOfTxFlagBytes = 14;
    this.bleDeviceId = info.bleDeviceId;
    this.deviceId = info.deviceId;
    this.deviceType = info.deviceType;
    this.deviceTypeImg = DeviceTypeToImgUrl(info.deviceType);
    this.bleSignalStrength = shallowRef(BleRssiToSignalStrength(info.rssi));
    this.bleSignalStrengthImg = shallowRef(BleSignalStrengthToImgUrl(this.bleSignalStrength.value));
    this.bleSignalStrengthColor = shallowRef(BleSignalStrengthToColor(this.bleSignalStrength.value));
    this.signalStrengthHistory.push({ value: this.bleSignalStrength.value, timestamp: new Date() });
    this.manufacturerData = info.manufacturerData;
    this.createdAt = new Date();
    this.useMetric = useMetric;
    this.registerMessages();
  }

  protected abstract parseManufacturerData(bytes: ByteList): void;
  protected abstract registerMessages(): void;

  update(device: VisibleDevice) {
    this.manufacturerData = device.manufacturerData;
    this.updateRssi(device.rssi);
    this.updatedAt = new Date();
    this.parseManufacturerData(this.manufacturerData);
  }

  async connect(opts?: { timeout?: number }) {
    this.onConnecting();
    try {
      await BleClient.connect(
        this.bleDeviceId,
        (bleDeviceId) => {
          if (bleDeviceId === this.bleDeviceId) {
            this.onDisconnected();
          }
        },
        { timeout: opts?.timeout }
      );
      this.onConnected();
    } catch (e) {
      this.onConnectionError(e);
    }
  }

  async disconnect() {
    if (this.connectionState.value === 'CONNECTED') {
      await BleClient.stopNotifications(this.bleDeviceId, this.serviceId, this.notifyCharacteristicId);
    }
    await BleClient.disconnect(this.bleDeviceId);
  }

  isStillVisible(opts?: { timeoutSeconds?: number }): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(() => {
        BleClient.stopLEScan();
        resolve(false);
      }, (opts?.timeoutSeconds || 3) * 1000);
      BleClient.requestLEScan({ services: [this.serviceId] }, (res) =>
        res.device.deviceId === this.bleDeviceId ? resolve(true) : null
      );
    });
  }

  protected onConnecting() {
    this.connectionState.value = 'CONNECTING';
  }

  protected onConnected() {
    this.connectionState.value = 'CONNECTED';
    BleClient.startNotifications(this.bleDeviceId, this.serviceId, this.notifyCharacteristicId, (data) => {
      this.receivedBytes(new ByteList(Buffer.from(data.buffer)));
    });

    BleClient.getServices(this.bleDeviceId).then((services) => {
      let pduCharacteristic: BleCharacteristic | undefined;
      let mtuCharacteristic: BleCharacteristic | undefined;
      let phyCharacteristic: BleCharacteristic | undefined;
      for (const service of services) {
        if (service.uuid.toUpperCase().trim() === this.serviceId) {
          for (const characteristic of service.characteristics) {
            const uuid = characteristic.uuid.toUpperCase().trim();
            if (uuid === BLE_SERVICES.WALK_BY.CHARACTERISTICS.PDU) {
              pduCharacteristic = characteristic;
            } else if (uuid === BLE_SERVICES.WALK_BY.CHARACTERISTICS.MTU) {
              mtuCharacteristic = characteristic;
            } else if (uuid === BLE_SERVICES.WALK_BY.CHARACTERISTICS.PHY) {
              phyCharacteristic = characteristic;
            }
          }
        }
      }

      if (pduCharacteristic && mtuCharacteristic && phyCharacteristic) {
        BleClient.startNotifications(this.bleDeviceId, this.serviceId, pduCharacteristic.uuid, (data) => {
          this.pduSize = data.getUint16(0, true);
          this.calculateTxSize();
        });
        BleClient.startNotifications(this.bleDeviceId, this.serviceId, mtuCharacteristic.uuid, (data) => {
          this.mtuSize = data.getUint16(0, true);
          this.calculateTxSize();
        });
        BleClient.startNotifications(this.bleDeviceId, this.serviceId, phyCharacteristic.uuid, (data) => {
          this.phy = (data.getUint8(0) & 0xf) as BleAntenna;
          this.calculateTxSize();
        });
        BleClient.read(this.bleDeviceId, this.serviceId, pduCharacteristic.uuid).then((data) => {
          this.pduSize = data.getUint16(0, true);
          this.calculateTxSize();
        });
        BleClient.read(this.bleDeviceId, this.serviceId, mtuCharacteristic.uuid).then((data) => {
          this.mtuSize = data.getUint16(0, true);
          this.calculateTxSize();
        });
        BleClient.read(this.bleDeviceId, this.serviceId, phyCharacteristic.uuid).then((data) => {
          this.phy = (data.getUint8(0) & 0xf) as BleAntenna;
          this.calculateTxSize();
        });
      }
    });

    this.runPeriodicChecks();
  }

  protected onDisconnected() {
    this.connectionState.value = 'DISCONNECTED';
    this.pduSize = DEFAULT_PDU_SIZE;
    this.mtuSize = DEFAULT_MTU_SIZE;
    this.txSize = DEFAULT_MTU_SIZE - NOTIFICATION_GATT_HEADER_SIZE;
    this.phy = BleAntenna.UNKNOWN;
    this.sendTimeUs = DEFAULT_PDU_SIZE * 8 + IFS_TIME_US;
  }

  protected onConnectionError(error: unknown) {
    this.connectionState.value = 'ERROR';
    this.pduSize = DEFAULT_PDU_SIZE;
    this.mtuSize = DEFAULT_MTU_SIZE;
    this.txSize = DEFAULT_MTU_SIZE - NOTIFICATION_GATT_HEADER_SIZE;
    this.phy = BleAntenna.UNKNOWN;
    this.sendTimeUs = DEFAULT_PDU_SIZE * 8 + IFS_TIME_US;
  }

  protected checkResponse(msg?: Message) {
    const now = new Date();
    const keepWaitingFor: Waiter[] = [];
    for (const w of this.waitingForResponse) {
      if (w.timeOutAt < now) {
        w.resolve();
      } else if (msg && w.match(msg)) {
        w.resolve(msg);
      } else {
        keepWaitingFor.push(w);
      }
    }
    this.waitingForResponse = keepWaitingFor;
  }

  protected async sendMessage<T extends Message = Message>(
    msg: Message,
    waitForResponse?: { match: (res: T) => boolean; timeOutSeconds?: number; retries?: number }
  ) {
    const bytes = await this.getBytes(msg);
    const sendAttempts = 1 + (!waitForResponse ? 0 : waitForResponse.retries || DEFAULT_RETRY_COUNT);
    let res: T | undefined = undefined;
    for (let i = 0; i < sendAttempts; i++) {
      await this.sendBytes(bytes);
      res = await this.getResponsePromise<T>(waitForResponse?.match, waitForResponse?.timeOutSeconds);
      if (res) {
        break;
      }
    }
    return res;
  }

  private getResponsePromise<T extends Message = Message>(
    match?: (res: T) => boolean,
    timeOutSeconds = DEFAULT_TIMEOUT_SECONDS
  ) {
    return new Promise<T | undefined>((resolve) => {
      if (!match) {
        return resolve(undefined);
      }
      const timeOutAt = new Date();
      timeOutAt.setSeconds(timeOutAt.getSeconds() + timeOutSeconds);
      this.waitingForResponse.push({
        resolve: resolve as (res?: Message) => void,
        timeOutAt,
        match: match as (res: Message) => boolean,
      });
    });
  }

  private async getBytes(msg: Message) {
    if (!this.useEncryption) {
      return this.messageService.serialize(msg);
    } else {
      return this.messageService.serialize(msg, (data: Buffer, frameId?: number) => {
        return NcssEncryption.encrypt({
          data: Array.from(data),
          frameId: frameId || 0,
          deviceId: this.deviceId,
          version: this.encryptionKey || NcssEncryptionVersion.GW4,
        }).then((res) => {
          return Buffer.from(res.data);
        });
      });
    }
  }

  private async sendBytes(bytes: ByteList) {
    const index = this.txBytes.index;
    this.txBytes.concat(bytes);
    this.txBytes.index = index;

    while (this.txBytes.index < this.txBytes.length) {
      const index = this.txBytes.index;
      const data = numbersToDataView(Array.from(this.txBytes.getBuffer().subarray(index, index + this.txSize)));
      this.txBytes.trimLeft(data.byteLength);
      await BleClient.writeWithoutResponse(this.bleDeviceId, this.serviceId, this.writeCharacteristicId, data);
      await this.delay(this.sendTimeUs);
    }
    this.txBytes = new ByteList();
  }

  private receivedBytes(bytes: ByteList) {
    try {
      this.messageService.deserialize(bytes, (data, frameId) => {
        return NcssEncryption.decrypt({
          data: Array.from(data),
          frameId: frameId || 0,
          deviceId: this.deviceId,
          version: this.encryptionKey || NcssEncryptionVersion.GW4,
        }).then((res) => {
          return Buffer.from(res.data);
        });
      });
    } catch (err) {
      console.error('Error Deserializing Msg', err);
      captureException(err);
    }
  }

  private updateRssi(rssi: number) {
    this.signalStrengthHistory.push({ value: BleRssiToSignalStrength(rssi), timestamp: new Date() });
    this.bleSignalStrength.value = this.computeBleSignalStrengthAverage();
    this.bleSignalStrengthImg.value = BleSignalStrengthToImgUrl(this.bleSignalStrength.value);
    this.bleSignalStrengthColor.value = BleSignalStrengthToColor(this.bleSignalStrength.value);
  }

  private computeBleSignalStrengthAverage() {
    const threshold = new Date();
    threshold.setSeconds(threshold.getSeconds() - AVG_SIGNAL_OVER_SECONDS);
    this.signalStrengthHistory = this.signalStrengthHistory.filter((v) => v.timestamp > threshold);
    return +(
      this.signalStrengthHistory.reduce((sum, v) => sum + v.value, 0) / this.signalStrengthHistory.length
    ).toFixed(0);
  }

  private async runPeriodicChecks() {
    if (this.connectionState.value !== 'CONNECTED') {
      clearTimeout(this.periodicChecksTimeout);
      return;
    }
    let rssi: number | undefined = undefined;
    try {
      rssi = await BleClient.readRssi(this.bleDeviceId);
    } catch (e) {
      console.log('Error reading Rssi', e);
    }
    if (rssi) {
      this.updateRssi(rssi);
    }
    this.checkResponse();
    this.periodicChecksTimeout = setTimeout(() => this.runPeriodicChecks(), 1000);
  }

  private calculateTxSize() {
    // Split the data over multiple over the air packets
    if (this.pduSize <= this.mtuSize) {
      this.txSize = Math.floor(
        this.pduSize -
        (L2CAP_HEADER_SIZE + NOTIFICATION_GATT_HEADER_SIZE) +
        ((this.mtuSize -
          NOTIFICATION_GATT_HEADER_SIZE -
          this.pduSize +
          (L2CAP_HEADER_SIZE + NOTIFICATION_GATT_HEADER_SIZE)) /
          this.pduSize) *
        this.pduSize
      );
    } else {
      // Single over-the-air packet, but accommodate room for headers.
      if (this.pduSize - this.mtuSize <= L2CAP_HEADER_SIZE) {
        this.txSize = this.pduSize - (L2CAP_HEADER_SIZE + NOTIFICATION_GATT_HEADER_SIZE);
      } else {
        // Room for the whole MTU, payload is MTU - Header
        this.txSize = this.mtuSize - NOTIFICATION_GATT_HEADER_SIZE;
      }
    }

    if (this.phy == BleAntenna.PHY_2MBPS) {
      this.sendTimeUs = this.pduSize * 4 + IFS_TIME_US;
    } else {
      this.sendTimeUs = this.pduSize * 8 + IFS_TIME_US;
    }
    console.log(
      `PDU: ${this.pduSize} MTU: ${this.mtuSize} TxSize: ${this.txSize} Phy: ${this.phy} SendTimeUs: ${this.sendTimeUs}`
    );
  }

  private delay(us: number) {
    const before = performance.now();
    let now = 0;
    let diff = 0;
    const delay_ms = us / 1000;
    do {
      now = performance.now();
      diff = now - before;
    } while (diff <= delay_ms);

    return Promise.resolve();
  }
}
